import { ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, Signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { RouterLink } from '@angular/router';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { CooperationActionsService } from '@app/shared/services/actions/cooperation-actions.service';
import { CooperationsDataService } from '@app/shared/services/cooperations-data.service';
import { CooperationStatusEnum } from '@app/shared/services/dtos/cooperation/cooperation-status.enum';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { Cooperation } from '@app/shared/types/models/cooperation/cooperation.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-company-cooperations-list-item',
  standalone: true,
  imports: [MatIconModule, TranslatePipe, UiButtonComponent, MatMenuModule, RouterLink],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './company-cooperations-list-item.component.html'
  // styleUrl: './company-cooperations-list-item.component.css'
})
export class CompanyCooperationsListItemComponent {
  private readonly cooperationsActionsService = inject(CooperationActionsService);
  private readonly cooperationsDataService = inject(CooperationsDataService);
  private readonly dialogService = inject(DialogService);
  private readonly translateService = inject(TranslateService);

  protected readonly CooperationStatusEnum = CooperationStatusEnum;

  protected partnerName: Signal<string> = computed(() =>
    this.type() === 'incoming'
      ? (this.cooperation()?.requestorCompany()?.name ?? '')
      : (this.cooperation()?.receipientCompany()?.name ?? '')
  );

  protected statusLabel: Signal<string> = computed(() =>
    this.cooperationsDataService.getStatusLabelKey(this.cooperation()?.status)
  );

  public readonly cooperation: InputSignal<Cooperation> = input.required<Cooperation>();
  public readonly companyId: InputSignal<string> = input.required<string>();
  public readonly type: InputSignal<'incoming' | 'outgoing'> = input<'incoming' | 'outgoing'>('outgoing');

  protected accept(): void {
    this.cooperationsActionsService.accept(this.cooperation().id, this.companyId()).subscribe();
  }

  protected reject(): void {
    this.cooperationsActionsService.reject(this.cooperation().id, this.companyId()).subscribe();
  }

  protected openDeactivateConfirmationDialog(): void {
    this.dialogService
      .openConfirmationDialog({
        title: this.translateService.instant('cooperations.deactivate-confirmation-title'),
        desc: this.translateService.instant('cooperations.deactivate-confirmation-desc')
      })
      .afterClosed()
      .subscribe(decision => {
        if (decision) {
          this.cooperationsActionsService.deactivate(this.cooperation().id, this.companyId()).subscribe();
        }
      });
  }

  protected preventNavigation(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }
}
